import { useEffect, useRef, useState } from 'react'
import type { AddressResult } from '../types'

interface MapViewProps {
  fromAddress: AddressResult | null
  toAddress: AddressResult | null
  intermediateAddresses?: AddressResult[]
  waitingTime?: string | null
  arrivalTime?: string | null
}

export const MapView = ({ 
  fromAddress, 
  toAddress,
  intermediateAddresses = [],
  waitingTime,
  arrivalTime,
}: MapViewProps) => {
  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<L.Map | null>(null)
  const markersRef = useRef<L.Marker[]>([])
  const polylineRef = useRef<L.Polyline | null>(null)
  const [isLoaded, setIsLoaded] = useState(false)

  // Load Leaflet
  useEffect(() => {
    if (typeof window !== 'undefined' && !window.L) {
      setIsLoaded(true)
    } else {
      setIsLoaded(true)
    }
  }, [])

  // Initialize map
  useEffect(() => {
    if (!isLoaded || !mapRef.current || mapInstanceRef.current) return

    const L = window.L
    if (!L) return

    // Default center (Moscow)
    const center: [number, number] = [55.7558, 37.6173]
    
    mapInstanceRef.current = L.map(mapRef.current, {
      center,
      zoom: 11,
      zoomControl: false,
      attributionControl: false,
    })

    // Dark tile layer (стилизованная карта)
    L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
      maxZoom: 19,
    }).addTo(mapInstanceRef.current)

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove()
        mapInstanceRef.current = null
      }
    }
  }, [isLoaded])

  // Update markers
  useEffect(() => {
    if (!mapInstanceRef.current || !isLoaded) return
    const L = window.L
    if (!L) return

    const map = mapInstanceRef.current

    // Clear old markers
    markersRef.current.forEach(m => m.remove())
    markersRef.current = []
    if (polylineRef.current) {
      polylineRef.current.remove()
      polylineRef.current = null
    }

    const points: [number, number][] = []

    // From marker с бейджем времени ожидания
    if (fromAddress) {
      const waitingBadge = waitingTime 
        ? `<div style="
            position: absolute;
            top: -40px;
            left: 50%;
            transform: translateX(-50%);
            background: linear-gradient(135deg, #9362CC, #635BFF);
            color: white;
            padding: 4px 10px;
            border-radius: 12px;
            font-size: 12px;
            font-weight: 600;
            white-space: nowrap;
            box-shadow: 0 4px 12px rgba(147, 98, 204, 0.4);
          ">${waitingTime}</div>`
        : ''

      const icon = L.divIcon({
        className: 'custom-marker-from',
        html: `
          <div style="position: relative;">
            ${waitingBadge}
            <div style="
              width: 40px; 
              height: 40px;
              background: linear-gradient(135deg, #22C55E, #16A34A);
              border-radius: 50%;
              display: flex; 
              align-items: center; 
              justify-content: center;
              box-shadow: 0 4px 16px rgba(34, 197, 94, 0.5);
              border: 3px solid rgba(255,255,255,0.9);
              position: relative;
            ">
              <div style="
                width: 12px;
                height: 12px;
                background: white;
                border-radius: 50%;
              "></div>
            </div>
            <div style="
              width: 3px;
              height: 12px;
              background: linear-gradient(to bottom, #22C55E, transparent);
              margin: 0 auto;
            "></div>
          </div>
        `,
        iconSize: [40, 60],
        iconAnchor: [20, 52],
      })
      
      const marker = L.marker([fromAddress.lat, fromAddress.lon], { icon }).addTo(map)
      markersRef.current.push(marker)
      points.push([fromAddress.lat, fromAddress.lon])
    }

    // Промежуточные точки
    intermediateAddresses.forEach((addr, index) => {
      const icon = L.divIcon({
        className: 'custom-marker-intermediate',
        html: `
          <div style="position: relative;">
            <div style="
              position: absolute;
              top: -30px;
              left: 50%;
              transform: translateX(-50%);
              background: rgba(147, 98, 204, 0.9);
              color: white;
              padding: 2px 8px;
              border-radius: 8px;
              font-size: 10px;
              font-weight: 600;
              white-space: nowrap;
            ">${index + 1}</div>
            <div style="
              width: 28px; 
              height: 28px;
              background: linear-gradient(135deg, #9362CC, #9362CC);
              border-radius: 50%;
              display: flex; 
              align-items: center; 
              justify-content: center;
              box-shadow: 0 4px 12px rgba(147, 98, 204, 0.4);
              border: 2px solid rgba(255,255,255,0.8);
            ">
              <div style="
                width: 8px;
                height: 8px;
                background: white;
                border-radius: 50%;
              "></div>
            </div>
          </div>
        `,
        iconSize: [28, 40],
        iconAnchor: [14, 40],
      })
      
      const marker = L.marker([addr.lat, addr.lon], { icon }).addTo(map)
      markersRef.current.push(marker)
      points.push([addr.lat, addr.lon])
    })

    // To marker с бейджем времени прибытия
    if (toAddress) {
      const arrivalBadge = arrivalTime
        ? `<div style="
            position: absolute;
            top: -40px;
            left: 50%;
            transform: translateX(-50%);
            background: rgba(2, 2, 9, 0.9);
            color: white;
            padding: 4px 10px;
            border-radius: 12px;
            font-size: 12px;
            font-weight: 500;
            white-space: nowrap;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
            border: 1px solid rgba(147, 98, 204, 0.3);
          ">прибытие в ${arrivalTime}</div>`
        : ''

      const icon = L.divIcon({
        className: 'custom-marker-to',
        html: `
          <div style="position: relative;">
            ${arrivalBadge}
            <div style="
              width: 40px; 
              height: 40px;
              background: linear-gradient(135deg, #9362CC, #7043B8);
              border-radius: 50%;
              display: flex; 
              align-items: center; 
              justify-content: center;
              box-shadow: 0 4px 16px rgba(147, 98, 204, 0.5);
              border: 3px solid rgba(255,255,255,0.9);
            ">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
                <circle cx="12" cy="10" r="3" fill="white"/>
              </svg>
            </div>
            <div style="
              width: 3px;
              height: 12px;
              background: linear-gradient(to bottom, #9362CC, transparent);
              margin: 0 auto;
            "></div>
          </div>
        `,
        iconSize: [40, 60],
        iconAnchor: [20, 52],
      })
      
      const marker = L.marker([toAddress.lat, toAddress.lon], { icon }).addTo(map)
      markersRef.current.push(marker)
      points.push([toAddress.lat, toAddress.lon])
    }

    // Draw route line с градиентом
    if (points.length >= 2) {
      // Создаём анимированную линию маршрута
      polylineRef.current = L.polyline(points, {
        color: '#9362CC',
        weight: 5,
        opacity: 0.9,
        lineCap: 'round',
        lineJoin: 'round',
        dashArray: '1, 12',
        dashOffset: '0',
      }).addTo(map)

      // Добавляем основную линию
      L.polyline(points, {
        color: '#635BFF',
        weight: 3,
        opacity: 0.6,
        lineCap: 'round',
        lineJoin: 'round',
      }).addTo(map)

      // Fit bounds с отступами
      const bounds = L.latLngBounds(points)
      map.fitBounds(bounds, { 
        padding: [80, 80],
        maxZoom: 15,
      })
    } else if (points.length === 1) {
      map.setView(points[0], 15)
    }
  }, [fromAddress, toAddress, intermediateAddresses, waitingTime, arrivalTime, isLoaded])

  return (
    <div className="absolute inset-0 overflow-hidden">
      <div ref={mapRef} className="h-full w-full" style={{ zIndex: 0 }} />
      
      {/* Gradient overlays */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-dark-tertiary via-dark-tertiary/80 to-transparent pointer-events-none" style={{ zIndex: 1 }} />
      <div className="absolute top-0 left-0 right-0 h-20 bg-gradient-to-b from-dark-tertiary/90 to-transparent pointer-events-none" style={{ zIndex: 1 }} />

      {/* Кнопки управления картой */}
      <div className="absolute right-4 top-1/2 -translate-y-1/2 flex flex-col gap-2" style={{ zIndex: 2 }}>
        {/* Кнопка геолокации */}
        <button 
          className="w-11 h-11 rounded-xl bg-dark-secondary/90 backdrop-blur border border-primary/10 flex items-center justify-center text-text-secondary hover:text-primary-light hover:border-primary/30 transition-all shadow-lg"
          onClick={() => {
            // Запрос геолокации
            if (navigator.geolocation && mapInstanceRef.current) {
              navigator.geolocation.getCurrentPosition((pos) => {
                mapInstanceRef.current?.setView([pos.coords.latitude, pos.coords.longitude], 15)
              })
            }
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3"/>
            <path d="M12 2v2M12 20v2M2 12h2M20 12h2"/>
          </svg>
        </button>

        {/* Zoom in */}
        <button 
          className="w-11 h-11 rounded-xl bg-dark-secondary/90 backdrop-blur border border-primary/10 flex items-center justify-center text-text-secondary hover:text-primary-light hover:border-primary/30 transition-all shadow-lg"
          onClick={() => mapInstanceRef.current?.zoomIn()}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
        </button>

        {/* Zoom out */}
        <button 
          className="w-11 h-11 rounded-xl bg-dark-secondary/90 backdrop-blur border border-primary/10 flex items-center justify-center text-text-secondary hover:text-primary-light hover:border-primary/30 transition-all shadow-lg"
          onClick={() => mapInstanceRef.current?.zoomOut()}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
        </button>
      </div>

      {/* Кнопка центрирования на маршруте */}
      {fromAddress && toAddress && (
        <button
          className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-xl bg-dark-secondary/90 backdrop-blur border border-primary/10 flex items-center justify-center text-text-secondary hover:text-primary-light hover:border-primary/30 transition-all shadow-lg"
          style={{ zIndex: 2 }}
          onClick={() => {
            if (mapInstanceRef.current && fromAddress && toAddress) {
              const L = window.L
              if (L) {
                const allPoints: [number, number][] = [
                  [fromAddress.lat, fromAddress.lon],
                  ...intermediateAddresses.map(a => [a.lat, a.lon] as [number, number]),
                  [toAddress.lat, toAddress.lon]
                ]
                const bounds = L.latLngBounds(allPoints)
                mapInstanceRef.current.fitBounds(bounds, { padding: [80, 80] })
              }
            }
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2"/>
            <path d="M9 3v18M15 3v18M3 9h18M3 15h18"/>
          </svg>
        </button>
      )}
    </div>
  )
}

// Declare Leaflet on window
declare global {
  interface Window {
    L: typeof import('leaflet')
  }
}

export default MapView
